import { useId, useState } from 'react';
import { cn } from '../lib/cn';
import { CopyButton } from './copy-button';

export interface CodeBlockProps {
  code: string;
  /** Shown in the corner — a filename, or the language. */
  language?: string;
  className?: string;
}

/**
 * A snippet the user is expected to run, with the copy button they will want.
 *
 * Deliberately not syntax-highlighted: highlighting a curl invocation and a
 * Python file correctly means shipping a tokenizer, and the thing that
 * actually makes these usable is the copy button and a mono face that
 * disambiguates the characters in a key.
 */
export function CodeBlock({ code, language, className }: CodeBlockProps) {
  return (
    <div className={cn('group relative overflow-hidden rounded-lg border border-border bg-muted/40', className)}>
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="text-xs font-medium text-muted-foreground">{language ?? 'shell'}</span>
        <CopyButton value={code} />
      </div>
      <pre className="overflow-x-auto p-4 text-xs leading-relaxed">
        <code className="font-mono">{code}</code>
      </pre>
    </div>
  );
}

export interface CodeTab {
  id: string;
  label: string;
  language?: string;
  code: string;
}

/**
 * The same call in several languages.
 *
 * A quickstart that shows only curl makes every reader translate it; showing
 * every language at once makes the page a wall. Tabs are the compromise, and
 * the selection is local state because there is nothing to persist across a
 * page nobody visits twice.
 */
export function CodeTabs({ tabs, className }: { tabs: CodeTab[]; className?: string }) {
  const [active, setActive] = useState(tabs[0]?.id ?? '');
  const groupId = useId();
  const current = tabs.find((tab) => tab.id === active) ?? tabs[0];

  if (!current) return null;

  // The tab role promises arrow-key navigation, so it has to be implemented
  // rather than only declared — a role that announces an interaction the
  // component does not support is worse than plain buttons.
  function onKeyDown(event: React.KeyboardEvent) {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;
    event.preventDefault();
    const index = tabs.findIndex((tab) => tab.id === active);
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next) {
      setActive(next.id);
      document.getElementById(`${groupId}-tab-${next.id}`)?.focus();
    }
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-muted/40', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border pl-1 pr-2">
        <div role="tablist" onKeyDown={onKeyDown} className="flex items-center">
          {tabs.map((tab) => {
            const selected = tab.id === current.id;
            return (
              <button
                key={tab.id}
                id={`${groupId}-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`${groupId}-panel-${tab.id}`}
                // Roving focus: only the selected tab is in the tab order, and
                // the arrows move between them from there.
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(tab.id)}
                className={cn(
                  'border-b-2 border-transparent px-3 py-2 text-xs font-medium text-muted-foreground transition-colors',
                  'hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                  selected && 'border-primary text-foreground',
                )}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <CopyButton value={current.code} />
      </div>
      <pre
        id={`${groupId}-panel-${current.id}`}
        role="tabpanel"
        aria-labelledby={`${groupId}-tab-${current.id}`}
        tabIndex={0}
        className="overflow-x-auto p-4 text-xs leading-relaxed focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
      >
        <code className="font-mono">{current.code}</code>
      </pre>
    </div>
  );
}
